import React, { useState, useEffect } from "react";
import { CiFilter } from "react-icons/ci";
import { ColorRing } from "react-loader-spinner";
import Pagination from "@mui/material/Pagination";

import ListHeader from "../../../../components/ListHeader";
import RowsPerPageSelector from "../../../../components/Tables/Pagination/RowsPerPageSelector";
import API_BASE_URL from "../../../../Constants";

import FilterInspectorModal from "./FilterInspectorModal";
import InspectorEntryForm from "./InspectorsEntryForm";
import ViewInspectorModal from "./ViewInspectorModal";

//
// UPDATED: Full list of new fields in the table
//
const InspectorListFields = [
  { key: "inspectorName", label: "Inspector Name" },
  { key: "certificationNumber", label: "Certification #" },
  { key: "companyId", label: "Company ID" },
  { key: "isDirector", label: "Is Director?" },
  // NEW FIELDS:
  { key: "employeeId", label: "Employee ID" },
  { key: "serviceCompanyId", label: "SC ID" },
  { key: "serviceCompanyType", label: "SC Type" },
  { key: "certificationType", label: "Cert Type" },
  { key: "certificationLevel", label: "Cert Level" },
  { key: "certifiedBy", label: "Certified By" },
  { key: "inspectorStatus", label: "Status" },
  { key: "certificationDate", label: "Cert Date" },
  { key: "expirationDate", label: "Exp Date" },
  { key: "dobNowPassword", label: "DOB Now Pwd" },
  { key: "email", label: "Email" },
  { key: "otherEmail", label: "Other Email" },
  { key: "remoteDevice", label: "Remote Device" },
  { key: "phone", label: "Phone" },
];

const InspectorsList = () => {
  const [inspectors, setInspectors] = useState([]);
  const [filteredInspectors, setFilteredInspectors] = useState([]);

  const [viewMode, setViewMode] = useState("table");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage] = useState(25);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedInspector, setSelectedInspector] = useState(null);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all Inspectors
  const fetchInspectors = async () => {
    try {
      setIsLoading(true);
      const resp = await fetch(`${API_BASE_URL}/api/inspectors`, {
        credentials: "include",
      });
      if (!resp.ok) {
        throw new Error("Failed to fetch inspectors");
      }
      const data = await resp.json();
      setInspectors(Array.isArray(data) ? data : []);
      setFilteredInspectors(Array.isArray(data) ? data : []);
      setTotalPages(1); // or if you want, manually compute pages
      setCurrentPage(1);
    } catch (err) {
      console.error("Error fetching inspectors:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInspectors();
  }, []);

  // Sorting
  useEffect(() => {
    if (!sortConfig.key) return;
    const sorted = [...filteredInspectors].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";
      if (sortConfig.direction === "ascending") {
        return ("" + aValue).localeCompare("" + bValue);
      } else {
        return ("" + bValue).localeCompare("" + aValue);
      }
    });
    setFilteredInspectors(sorted);
  }, [sortConfig]);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Searching
  const handleSearch = () => {
    const lowerQuery = searchQuery.toLowerCase();
    const filtered = inspectors.filter((insp) =>
      insp.inspectorName?.toLowerCase().includes(lowerQuery)
    );
    setFilteredInspectors(filtered);
    setCurrentPage(1);
  };

  // Create Inspector Modal
  const handleOpenCreateModal = () => setIsCreateOpen(true);
  const handleCloseCreateModal = () => setIsCreateOpen(false);

  const refreshInspectors = () => {
    fetchInspectors();
  };

  // Filter Modal
  const toggleFilterModal = () => setIsFilterModalOpen(!isFilterModalOpen);

  // View Inspector Modal
  const handleOpenViewModal = (inspector) => {
    setSelectedInspector(inspector);
    setIsViewOpen(true);
  };

  const handleCloseViewModal = () => {
    setSelectedInspector(null);
    setIsViewOpen(false);
  };

  return (
    <div className="mx-auto pl-4 pr-4 h-screen w-full">
      <ListHeader
        title="Inspectors List"
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        handleSearchChange={(e) => setSearchQuery(e.target.value)}
        viewMode={viewMode}
        handleViewModeChange={setViewMode}
        handleOpenCreateModal={handleOpenCreateModal}
      >
        <button
          className="py-2 px-3 border-sss-blue-sel rounded-lg flex border text-sss-blue-sel font-bold hover:bg-sss-blue hover:text-white transition duration-300 ease-in-out"
          onClick={toggleFilterModal}
        >
          <CiFilter className="text-2xl" /> Filter
        </button>
      </ListHeader>

      {isLoading && (
        <div className="flex justify-center items-center">
          <ColorRing
            visible={isLoading}
            height="200"
            width="200"
            ariaLabel="color-ring-loading"
            colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
          />
        </div>
      )}

      {/* TABLE VIEW */}
      {viewMode === "table" && !isLoading && (
        <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
          <thead className="bg-gray-200">
            <tr>
              <th
                key="actions"
                className="px-4 py-2 text-left font-bold text-sss-blue-sel"
              >
                Actions
              </th>
              {InspectorListFields.map((field) => (
                <th
                  key={field.key}
                  className="px-4 py-2 text-left font-bold text-sss-blue-sel cursor-pointer"
                  onClick={() => handleSort(field.key)}
                >
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredInspectors.map((inspector, index) => (
              <tr key={index} className="border-t">
                <td className="px-4 py-2">
                  <button
                    className="text-sss-info font-semibold hover:underline"
                    onClick={() => handleOpenViewModal(inspector)}
                  >
                    View
                  </button>
                </td>
                {InspectorListFields.map((field) => {
                  const value = inspector[field.key];
                  return (
                    <td key={field.key} className="px-4 py-2">
                      {value !== null && value !== undefined
                        ? value.toString()
                        : "N/A"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* CARD VIEW */}
      {viewMode === "card" && !isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
          {filteredInspectors.map((inspector, index) => (
            <div
              key={index}
              className="p-4 bg-white rounded-r-2xl shadow-md border-2 border-gray-200 flex"
            >
              <div className="w-1/4 flex items-center justify-center">
                <img
                  src="https://via.placeholder.com/150"
                  alt="Inspector Placeholder"
                  className="rounded-l-lg object-cover w-full h-full"
                />
              </div>
              <div className="w-2/4 pl-4 space-y-3 bg-gray-100 p-6 border">
                <p>
                  <strong>ID:</strong> {inspector.id || "Unknown"}
                </p>
                <h3 className="font-bold text-lg mb-2">
                  {inspector.inspectorName || "Unknown Inspector"}
                </h3>
                <p>
                  <strong>Cert #:</strong>{" "}
                  {inspector.certificationNumber || "N/A"}
                </p>
                <p>
                  <strong>Is Director?</strong>{" "}
                  {inspector.isDirector ? "Yes" : "No"}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {inspector.inspectorStatus || "Unknown"}
                </p>
              </div>
              <div className="w-1/4 flex flex-col items-center justify-center space-y-1 pb-4 border-t border-b border-r rounded-r-xl">
                <button
                  onClick={() => handleOpenViewModal(inspector)}
                  className="bg-transparent text-sss-blue-sel hover:font-semibold hover:cursor-pointer w-1/2 py-2"
                >
                  View
                </button>
                <button className="text-gray-600 w-1/2 py-2 rounded cursor-not-allowed">
                  Edit
                </button>
                <button className="text-gray-600 w-1/2 py-2 rounded cursor-not-allowed">
                  Actions
                </button>
                <button className="text-gray-600 w-1/2 py-2 rounded cursor-not-allowed">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center p-4 border-t border-gray-300 bg-white">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(event, value) => setCurrentPage(value)}
          color="primary"
          shape="rounded"
        />
        <div className="flex items-center">
          <RowsPerPageSelector />
        </div>
      </div>

      {/* Filter Modal */}
      <FilterInspectorModal
        open={isFilterModalOpen}
        handleClose={toggleFilterModal}
      />

      {/* Create Inspector Modal */}
      <InspectorEntryForm
        open={isCreateOpen}
        onClose={handleCloseCreateModal}
        refreshInspectors={refreshInspectors}
      />

      {/* View Inspector Modal */}
      <ViewInspectorModal
        open={isViewOpen}
        onClose={handleCloseViewModal}
        inspectorData={selectedInspector}
        refreshInspectors={refreshInspectors}
      />
    </div>
  );
};

export default InspectorsList;
